import React from 'react';
import { useCurrentUser } from '../../shared/auth';
import { AstraI18nProvider } from './AstraI18nProvider';
import { isMobileAstraEnabled } from './constants';
import { AstraProvider } from './conversation/AstraConversationProvider';

interface AstraMobileProviderProps {
  readonly children: React.ReactNode;
}

/**
 * Mounts the Astra chat stack for the mobile shell.
 *
 * Two providers, both scoped to this subtree:
 *   - `AstraProvider` — the conversation itself (messages, in-flight turn,
 *     session id). The same engine the desktop panel reads through `useAstra()`.
 *   - `AstraI18nProvider` — the react-i18next instance the shared
 *     `AgentChatPanel` translates through.
 *
 * Sits inside `MobileAuthGate` (so `useCurrentUser` resolves) and *outside*
 * the route table, so the conversation survives navigating away from `/chat`
 * and back — the same way the desktop panel keeps its thread across pages.
 *
 * When the stage flag is off the children render bare: no `/chat` route or
 * nav entry exists either, so nothing downstream calls `useAstra()`.
 */
export function AstraMobileProvider({
  children,
}: AstraMobileProviderProps): React.ReactElement {
  if (!isMobileAstraEnabled()) return <>{children}</>;
  return <EnabledAstraMobileProvider>{children}</EnabledAstraMobileProvider>;
}

function EnabledAstraMobileProvider({
  children,
}: AstraMobileProviderProps): React.ReactElement {
  const { alias, businessUnit } = useCurrentUser();
  return (
    <AstraProvider userId={alias} businessUnitId={businessUnit ?? ''} isAuthenticated>
      <AstraI18nProvider>{children}</AstraI18nProvider>
    </AstraProvider>
  );
}
